import { Injectable } from '@angular/core';

export interface FileIconInfo {
  icon: string;           // PrimeNG icon class
  color: string;          // CSS color class
  label: string;          // Short label (e.g., "PDF", "JPG", "DOC")
  bgColor: string;        // Background color class
}

@Injectable({
  providedIn: 'root'
})
export class FileIconService {
  
  /**
   * Get file icon information based on file URL or extension
   * @param fileUrl - The file URL or filename
   * @returns FileIconInfo with icon, color, label, and background color
   */
  getFileIcon(fileUrl: string): FileIconInfo {
    if (!fileUrl) {
      return this.getDefaultIcon();
    }

    // Extract file extension from URL
    const extension = this.getFileExtension(fileUrl).toLowerCase();
    
    // Map extensions to icon info
    const iconMap: { [key: string]: FileIconInfo } = {
      // PDF files
      'pdf': {
        icon: 'pi pi-file-pdf',
        color: 'text-red-500',
        label: 'PDF',
        bgColor: 'bg-red-500'
      },
      // Image files
      'jpg': {
        icon: 'pi pi-image',
        color: 'text-blue-500',
        label: 'JPG',
        bgColor: 'bg-blue-500'
      },
      'jpeg': {
        icon: 'pi pi-image',
        color: 'text-blue-500',
        label: 'JPG',
        bgColor: 'bg-blue-500'
      },
      'png': {
        icon: 'pi pi-image',
        color: 'text-green-500',
        label: 'PNG',
        bgColor: 'bg-green-500'
      },
      'gif': {
        icon: 'pi pi-image',
        color: 'text-purple-500',
        label: 'GIF',
        bgColor: 'bg-purple-500'
      },
      'webp': {
        icon: 'pi pi-image',
        color: 'text-orange-500',
        label: 'WEBP',
        bgColor: 'bg-orange-500'
      },
      'svg': {
        icon: 'pi pi-image',
        color: 'text-pink-500',
        label: 'SVG',
        bgColor: 'bg-pink-500'
      },
      // Word documents
      'doc': {
        icon: 'pi pi-file-word',
        color: 'text-blue-600',
        label: 'DOC',
        bgColor: 'bg-blue-600'
      },
      'docx': {
        icon: 'pi pi-file-word',
        color: 'text-blue-600',
        label: 'DOCX',
        bgColor: 'bg-blue-600'
      },
      // Excel files
      'xls': {
        icon: 'pi pi-file-excel',
        color: 'text-green-600',
        label: 'XLS',
        bgColor: 'bg-green-600'
      },
      'xlsx': {
        icon: 'pi pi-file-excel',
        color: 'text-green-600',
        label: 'XLSX',
        bgColor: 'bg-green-600'
      },
      // Text files
      'txt': {
        icon: 'pi pi-file',
        color: 'text-gray-600',
        label: 'TXT',
        bgColor: 'bg-gray-600'
      },
      // Archive files
      'zip': {
        icon: 'pi pi-file',
        color: 'text-yellow-600',
        label: 'ZIP',
        bgColor: 'bg-yellow-600'
      },
      'rar': {
        icon: 'pi pi-file',
        color: 'text-yellow-600',
        label: 'RAR',
        bgColor: 'bg-yellow-600'
      }
    };

    // Return specific icon if found, otherwise return default
    return iconMap[extension] || this.getDefaultIcon();
  }

  /**
   * Get file extension from URL or filename
   * @param fileUrl - The file URL or filename
   * @returns File extension without the dot
   */
  private getFileExtension(fileUrl: string): string {
    if (!fileUrl) return '';
    
    // Remove query parameters and hash
    const cleanUrl = fileUrl.split('?')[0].split('#')[0];
    
    // Extract filename from URL
    const filename = cleanUrl.split('/').pop() || cleanUrl;
    
    // Get extension
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1 || lastDot === filename.length - 1) {
      return '';
    }
    
    return filename.substring(lastDot + 1);
  }

  /**
   * Get default/general file icon for unknown file types
   * @returns Default FileIconInfo
   */
  private getDefaultIcon(): FileIconInfo {
    return {
      icon: 'pi pi-file',
      color: 'text-gray-500',
      label: 'FILE',
      bgColor: 'bg-gray-500'
    };
  }

  /**
   * Check if file is an image based on extension
   * @param fileUrl - The file URL or filename
   * @returns true if file is an image
   */
  isImageFile(fileUrl: string): boolean {
    const extension = this.getFileExtension(fileUrl).toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
    return imageExtensions.includes(extension);
  }

  /**
   * Check if file is a PDF based on extension
   * @param fileUrl - The file URL or filename
   * @returns true if file is a PDF
   */
  isPdfFile(fileUrl: string): boolean {
    const extension = this.getFileExtension(fileUrl).toLowerCase();
    return extension === 'pdf';
  }
}

